import {
  CUSTOM_BASE_PLAN_VALUE,
  UNASSIGNED_BASE_PLAN_FILTER_VALUE,
} from './basePlanSelection';
import {
  MANUAL_CONSULTANT_FILTER_PREFIX,
  UNASSIGNED_CONSULTANT_FILTER_VALUE,
  USER_CONSULTANT_FILTER_PREFIX,
} from './builtFilterOptions';

export const BUILT_FILTER_URL_VERSION_PARAM = 'fv';
export const BUILT_FILTER_URL_VERSION = '1';

export const BUILT_FILTER_GROUPS = {
  BUILD_STYLE: 'buildStyle',
  TYPE: 'type',
  REVIEW: 'review',
  BASE_PLAN: 'basePlan',
  CONSULTANT: 'consultant',
};

const GROUP_CONFIG = {
  [BUILT_FILTER_GROUPS.BUILD_STYLE]: { param: 'bs', codec: 'text' },
  [BUILT_FILTER_GROUPS.TYPE]: { param: 'ty', codec: 'text' },
  [BUILT_FILTER_GROUPS.REVIEW]: { param: 'rv', codec: 'text' },
  [BUILT_FILTER_GROUPS.BASE_PLAN]: { param: 'bp', codec: 'basePlan' },
  [BUILT_FILTER_GROUPS.CONSULTANT]: { param: 'sc', codec: 'consultant' },
};

const createAllSelection = () => ({ mode: 'all', values: new Set() });

export function createDefaultBuiltFilterUrlState() {
  return Object.fromEntries(Object.keys(GROUP_CONFIG).map((group) => [group, createAllSelection()]));
}

function encodeValue(codec, value) {
  if (codec === 'basePlan') {
    if (value === CUSTOM_BASE_PLAN_VALUE) return 'c';
    if (value === UNASSIGNED_BASE_PLAN_FILTER_VALUE) return 'u';
    if (value.startsWith('plan:')) return value.slice(5);
    return '';
  }

  if (codec === 'consultant') {
    if (value === UNASSIGNED_CONSULTANT_FILTER_VALUE) return 'u';
    if (value.startsWith(USER_CONSULTANT_FILTER_PREFIX)) {
      return value.slice(USER_CONSULTANT_FILTER_PREFIX.length);
    }
    if (value.startsWith(MANUAL_CONSULTANT_FILTER_PREFIX)) {
      return `m:${value.slice(MANUAL_CONSULTANT_FILTER_PREFIX.length)}`;
    }
    return '';
  }

  return value;
}

function decodeValue(codec, value) {
  if (!value) return '';
  if (codec === 'basePlan') {
    if (value === 'c') return CUSTOM_BASE_PLAN_VALUE;
    if (value === 'u') return UNASSIGNED_BASE_PLAN_FILTER_VALUE;
    return `plan:${value}`;
  }

  if (codec === 'consultant') {
    if (value === 'u') return UNASSIGNED_CONSULTANT_FILTER_VALUE;
    if (value.startsWith('m:')) return `${MANUAL_CONSULTANT_FILTER_PREFIX}${value.slice(2)}`;
    return `${USER_CONSULTANT_FILTER_PREFIX}${value}`;
  }

  return value;
}

export function readBuiltFilterUrlState(search = '') {
  const params = new URLSearchParams(search);
  const state = createDefaultBuiltFilterUrlState();
  if (params.get(BUILT_FILTER_URL_VERSION_PARAM) !== BUILT_FILTER_URL_VERSION) return state;

  Object.entries(GROUP_CONFIG).forEach(([group, { param, codec }]) => {
    const values = params.getAll(param);
    if (!values.length) return;
    const [encodedMode, ...encodedValues] = values;
    if (encodedMode === 'n') {
      state[group] = { mode: 'none', values: new Set() };
      return;
    }
    if (encodedMode !== 'i' && encodedMode !== 'x') return;

    const decodedValues = new Set(encodedValues.map((value) => decodeValue(codec, value)).filter(Boolean));
    state[group] = {
      mode: encodedMode === 'i' ? 'include' : 'exclude',
      values: decodedValues,
    };
  });

  return state;
}

export function isBuiltFilterValueSelected(selection, value) {
  if (!selection || selection.mode === 'all') return true;
  if (selection.mode === 'none') return false;
  if (selection.mode === 'include') return selection.values.has(value);
  return !selection.values.has(value);
}

export function getSelectedBuiltFilterValues(selection, optionValues) {
  return new Set(optionValues.filter((value) => isBuiltFilterValueSelected(selection, value)));
}

export function createAdaptiveBuiltFilterSelection(selectedValues, optionValues) {
  const options = [...new Set(optionValues)];
  const selected = new Set([...selectedValues].filter((value) => options.includes(value)));
  if (!options.length || selected.size === options.length) return createAllSelection();
  if (!selected.size) return { mode: 'none', values: new Set() };

  const excluded = options.filter((value) => !selected.has(value));
  return selected.size <= excluded.length
    ? { mode: 'include', values: selected }
    : { mode: 'exclude', values: new Set(excluded) };
}

export function pruneBuiltFilterSelection(selection, optionValues) {
  if (!selection || selection.mode === 'all' || selection.mode === 'none') return selection || createAllSelection();
  const validValues = new Set(optionValues);
  const values = new Set([...selection.values].filter((value) => validValues.has(value)));
  if (selection.mode === 'include' && !values.size) return { mode: 'none', values: new Set() };
  if (selection.mode === 'exclude' && !values.size) return createAllSelection();
  return { mode: selection.mode, values };
}

export function reconcileBuiltFilterSelection(selection, optionValues) {
  const pruned = pruneBuiltFilterSelection(selection, optionValues);
  const selectedValues = getSelectedBuiltFilterValues(pruned, optionValues);
  return createAdaptiveBuiltFilterSelection(selectedValues, optionValues);
}

export function areBuiltFilterSelectionsEqual(left, right) {
  if (left.mode !== right.mode || left.values.size !== right.values.size) return false;
  return [...left.values].every((value) => right.values.has(value));
}

export function writeBuiltFilterUrlState(url, state) {
  const nextUrl = new URL(url.toString());
  nextUrl.searchParams.delete(BUILT_FILTER_URL_VERSION_PARAM);
  Object.values(GROUP_CONFIG).forEach(({ param }) => nextUrl.searchParams.delete(param));

  let hasFilters = false;
  Object.entries(GROUP_CONFIG).forEach(([group, { param, codec }]) => {
    const selection = state[group] || createAllSelection();
    if (selection.mode === 'all') return;
    if (selection.mode === 'none') {
      hasFilters = true;
      nextUrl.searchParams.append(param, 'n');
      return;
    }

    const encodedValues = [...selection.values]
      .map((value) => encodeValue(codec, value))
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b));
    if (!encodedValues.length) return;
    hasFilters = true;
    nextUrl.searchParams.append(param, selection.mode === 'include' ? 'i' : 'x');
    encodedValues.forEach((value) => nextUrl.searchParams.append(param, value));
  });

  if (hasFilters) nextUrl.searchParams.set(BUILT_FILTER_URL_VERSION_PARAM, BUILT_FILTER_URL_VERSION);
  return nextUrl;
}
